// WHAT: Widget component for displaying contextual insights from Context Intelligence Layer
// WHY:  Provides dashboard overview of AI-generated insights with generation and refresh capabilities
// DOMAIN: Core → Dashboard → Widgets → Insights
// DEPENDENCIES: useContextInsights, useInsightGenerator hooks
// RISKS:
//   - NOTE: Double refresh (immediate + delayed) may cause unnecessary API calls
//   - NOTE: Console.log/error should be replaced with proper logging
//   - NOTE: Error message is hardcoded in German - may not match user language
//   - NOTE: handleDetailsClick is not implemented - may confuse users

import type { Insight } from "@/domains/core/context-intelligence/types/insights.types";

import { Lightbulb, AlertTriangle, RefreshCw } from "lucide-react";
import { type FC, useMemo } from "react";

import { Button } from "@/components/ui";
import { useContextInsights } from "@/domains/core/context-intelligence/hooks/useContextInsights";
import { useInsightGenerator } from "@/domains/core/context-intelligence/hooks/useInsightGenerator";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base/WidgetContainer";
import { InsightCard } from "./InsightCard";

/**
 * WHAT:
 *   Widget component that displays contextual insights from the Context Intelligence
 *   Layer with generation and refresh capabilities.
 *
 * WHY:
 *   Enables users to see AI-generated insights about their health and productivity
 *   data on the dashboard.
 *
 * BEHAVIOR:
 *   - Fetches top insights (limit: 5)
 *   - Provides generate insights button when no insights available
 *   - Displays insight cards with priority indicators
 *   - Handles loading and error states
 *   - Supports manual refresh
 *
 * RISKS:
 *   - NOTE: Double refresh (immediate + delayed) may cause unnecessary API calls
 *   - NOTE: Console.log/error should be replaced with proper logging
 *   - NOTE: Error message is hardcoded in German - may not match user language
 *   - NOTE: handleDetailsClick is not implemented - may confuse users
 *
 * @returns {JSX.Element} Context insights widget component
 *
 */
/**
 * WHAT:
 *   Handles insight details click.
 *
 * WHY:
 *   Enables viewing detailed insight information.
 *
 * NOTE: Not implemented - placeholder for future functionality
 *
 * @param _insight - Insight to view details for (unused, reserved for future)
 *
 */
const handleDetailsClick = (_insight: Insight) => {
  // NOTE: Reserved for future implementation - Open insight details modal via dedicated detail view component
  // Reason: Functionality will be implemented in future version
};

export const ContextInsightsWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.CONTEXT_INSIGHTS);

  // Stabilize query object to prevent re-renders
  const query = useMemo(() => ({ limit: 5 }), []);
  const { error, insights, loading, refresh, topInsights } = useContextInsights(query);
  const { generateInsights, hasData, isGenerating } = useInsightGenerator();

  /**
   * WHAT:
   *   Handles insight generation with refresh.
   *
   * WHY:
   *   Generates new insights and refreshes the list.
   *
   * BEHAVIOR:
   *   - Generates insights
   *   - Refreshes immediately
   *   - Refreshes again after 1s delay to catch delayed DB updates
   */
  const handleGenerateInsights = async () => {
    try {
      await generateInsights();
      // NOTE: Refresh insights after generation - wait a bit longer to ensure DB is updated
      // NOTE: Also force a refresh by calling it immediately and then again after delay
      refresh(); // NOTE: Immediate refresh
      setTimeout(() => {
        refresh(); // NOTE: Second refresh to catch any delayed updates
      }, 1000);
    } catch {
      // NOTE: Generation errors are handled by useInsightGenerator; widget stays in current state
    }
  };

  const hasCriticalInsights = useMemo(() => {
    return insights.some((index) => index.priority === "critical" || index.priority === "high");
  }, [insights]);

  return (
    <WidgetContainer
      description="Intelligent interpretations of your data"
      error={error ? "Fehler beim Laden der Insights" : null}
      icon={<Lightbulb className="w-5 h-5" />}
      isGenerating={isGenerating}
      loading={loading}
      onGenerate={() => {
        handleGenerateInsights().catch(() => {
          // Generation errors are handled by useInsightGenerator
        });
      }}
      onRefresh={() => {
        refresh();
      }}
      size={config?.size ?? "large"}
      title="Contextual Insights"
      widgetId={WIDGET_IDS.CONTEXT_INSIGHTS}
    >
      {/* Generate Button - Always show when no insights, even during loading */}
      {topInsights.length === 0 && (
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-400">
            {hasData
              ? "Click to analyze your data and generate insights"
              : "No data available yet. Connect your health and productivity sources first."}
          </p>
          {hasData ? (
            <Button
              className="flex items-center gap-2"
              disabled={isGenerating || loading}
              onClick={() => {
                handleGenerateInsights().catch(() => {
                  // Generation errors are handled by useInsightGenerator
                });
              }}
              size="sm"
              variant="outline"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? "animate-spin" : ""}`} />
              {isGenerating ? "Generating..." : "Generate Insights"}
            </Button>
          ) : null}
        </div>
      )}

      <div className="space-y-3 w-full">
        {topInsights.length === 0 && !loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            {hasCriticalInsights ? (
              <div className="flex flex-col items-center gap-2">
                <AlertTriangle className="w-8 h-8 text-yellow-400" />
                <p>No high-priority insights at the moment</p>
                <p className="text-xs">Check lower priority insights for more details</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Lightbulb className="w-8 h-8 text-gray-500" />
                <p>No insights available yet.</p>
                {hasData ? (
                  <p className="text-xs">
                    Click &quot;Generate Insights&quot; above to analyze your data.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          topInsights.map((insight) => (
            <InsightCard
              insight={insight}
              key={`${insight.id}-${String(insight.version)}`}
              onDetailsClick={handleDetailsClick}
            />
          ))
        )}
      </div>
    </WidgetContainer>
  );
};
